/**
 * Helper function to navigate to Pokemon detail page
 * Ensures we always have a valid identifier
 * 
 * @param {Object} pokemon - Pokemon object
 * @param {Function} navigate - React Router navigate function
 * @returns {boolean} - true if navigation was successful
 */
const SPECIAL_FORM_PATTERN = /-(mega(?:-x|-y)?|gmax|gigantamax|primal|alola|galar|hisui|paldea|rapid-strike|single-strike|crowned|eternamax|therian|origin|dusk-mane|dawn-wings|ice-rider|shadow-rider|bloodmoon|cornerstone|hearthflame|wellspring|teal-mask)$/;

/**
 * Build Pokémon detail URL supporting special forms using ?form=slug
 * Example: mawile-mega -> /pokemon/mawile?form=mawile-mega
 *
 * @param {Object|string} pokemonOrName - Pokemon object (with name/id) or slug string
 * @returns {string|null}
 */
export const getPokemonDetailPath = (pokemonOrName) => {
  const isString = typeof pokemonOrName === 'string';
  const rawName = isString
    ? pokemonOrName
    : (pokemonOrName?.name || pokemonOrName?.displayName || '');

  const slug = rawName
    ? rawName.toString().toLowerCase().trim().replace(/\s+/g, '-')
    : '';

  if (slug) {
    const formMatch = slug.match(SPECIAL_FORM_PATTERN);
    if (formMatch && formMatch.index > 0) {
      const baseName = slug.slice(0, formMatch.index);
      return `/pokemon/${baseName}?form=${slug}`;
    }

    return `/pokemon/${slug}`;
  }

  if (!isString) {
    const identifier = pokemonOrName?.id || pokemonOrName?.name;
    if (identifier) {
      return `/pokemon/${identifier}`;
    }
  }

  return null;
};

export const navigateToPokemon = (pokemon, navigate) => {
  if (!pokemon) {
    console.error('[Navigation] Pokemon object is undefined');
    return false;
  }

  const path = getPokemonDetailPath(pokemon);

  if (!path) {
    console.error('[Navigation] Pokemon has no valid route:', pokemon);
    return false;
  }

  console.log('[Navigation] Navigating to pokemon:', path);
  navigate(path);
  return true;
};

/**
 * Get a valid identifier from a pokemon object
 * @param {Object} pokemon - Pokemon object
 * @returns {string|number|null} - Valid identifier or null
 */
export const getPokemonIdentifier = (pokemon) => {
  if (!pokemon) return null;
  return pokemon.id || pokemon.name || null;
};